export interface LoShuNumberMeaning {
  title: string;
  element: string;
  direction: string;
  planet: string;
  meaning: string;
  guidance: string;
}

export interface LoShuArrow {
  id: string;
  name: string;
  numbers: number[];
  type: string;
  strength: string;
  weakness: string;
  present: boolean;
  missing: boolean;
  presentCount: number;
}

export interface LoShuNameCorrectionOption {
  suggestedName: string;
  addedLetter: string;
  addedLetterNumber: number;
  total: number;
  nameNumber: number;
  supportsMissingNumber: number;
  reason: string;
  naturalness: 'Good' | 'Average' | 'Weak';
  score: number;
}

export interface LoShuNameCorrection {
  currentName: string;
  currentTotal: number;
  currentNameNumber: number;
  missingNumbers: number[];
  lettersByMissingNumber: Record<number, string[]>;
  suggestions: LoShuNameCorrectionOption[];
  bestSuggestion: LoShuNameCorrectionOption | null;
  summary: string;
}

export interface LoShuResult {
  dob: string;
  method: string;
  fullName?: string;
  psychicNumber: number;
  destinyNumber: number;
  kuaNumber: number;
  personalDay: number;
  personalMonth: number;
  personalYear: number;
  nameNumber: number | null;
  nameCorrection: LoShuNameCorrection | null;
  friendlyNumbers: number[];
  nonFriendlyNumbers: number[];
  neutralNumbers: number[];
  luckyNumbers: number[];
  luckyColors: string[];
  driverConductorAnalysis: {
    title: string;
    overview: string;
    strengths: string;
    challenges: string;
    career: string;
    relationship: string;
    remedy: string;
  };
  birthDigits: number[];
  gridDigits: number[];
  grid: Record<number, number>;
  layout: number[][];
  numbers: Record<number, LoShuNumberMeaning>;
  presentNumbers: number[];
  missingNumbers: Array<LoShuNumberMeaning & { num: number; lesson: string }>;
  repeatedNumbers: Array<LoShuNumberMeaning & { num: number; count: number; interpretation: string }>;
  arrows: LoShuArrow[];
  strengthArrows: LoShuArrow[];
  weaknessArrows: LoShuArrow[];
  yogas: Array<{ id: string; name: string; numbers: number[]; effect: string }>;
  mysticalTriangles: Array<{ id: string; name: string; numbers: number[]; effect: string; active: boolean; presentCount: number }>;
  disclaimer: string;
}

export const LO_SHU_LAYOUT = [
  [4, 9, 2],
  [3, 5, 7],
  [8, 1, 6],
];

export const loShuNumberMeanings: Record<number, LoShuNumberMeaning> = {
  1: { title: 'Career and Initiative', element: 'Water', direction: 'North', planet: 'Sun', meaning: 'Independence, initiative, communication and career movement.', guidance: 'Use leadership with humility and keep decisions practical.' },
  2: { title: 'Relationships and Sensitivity', element: 'Earth', direction: 'South-West', planet: 'Moon', meaning: 'Cooperation, partnership, intuition and emotional sensitivity.', guidance: 'Build patience in relationships and avoid over-dependence.' },
  3: { title: 'Family and Expression', element: 'Wood', direction: 'East', planet: 'Jupiter', meaning: 'Wisdom, family support, creativity, learning and expression.', guidance: 'Convert ideas into steady learning and constructive communication.' },
  4: { title: 'Discipline and Wealth Effort', element: 'Wood', direction: 'South-East', planet: 'Rahu', meaning: 'Order, discipline, money planning, systems and practical effort.', guidance: 'Avoid shortcuts; routines and financial discipline help this number express well.' },
  5: { title: 'Balance and Center', element: 'Earth', direction: 'Center', planet: 'Mercury', meaning: 'Balance, adaptability, health of routines, communication and freedom.', guidance: 'Create structure without losing flexibility.' },
  6: { title: 'Mentors and Harmony', element: 'Metal', direction: 'North-West', planet: 'Venus', meaning: 'Support, mentors, responsibility, beauty, comfort and family harmony.', guidance: 'Balance comfort with duty and choose helpful associations.' },
  7: { title: 'Creativity and Inner Search', element: 'Metal', direction: 'West', planet: 'Ketu', meaning: 'Research, intuition, creativity, children, joy and inner questioning.', guidance: 'Use solitude for insight, not isolation.' },
  8: { title: 'Knowledge and Ambition', element: 'Earth', direction: 'North-East', planet: 'Saturn', meaning: 'Knowledge, ambition, organisation, patience and long-term growth.', guidance: 'Progress comes through persistence, planning and ethical effort.' },
  9: { title: 'Fame and Completion', element: 'Fire', direction: 'South', planet: 'Mars', meaning: 'Reputation, courage, ideals, visibility and humanitarian instinct.', guidance: 'Channel intensity into service, courage and disciplined action.' },
};

const arrowDefinitions = [
  { id: 'mental', name: 'Mental / Thought Plane', numbers: [4, 9, 2], type: 'row', strength: 'Clear thinking, memory, planning and decision clarity.', weakness: 'May need conscious work on planning, learning rhythm and mental focus.' },
  { id: 'emotional', name: 'Emotional / Heart Plane', numbers: [3, 5, 7], type: 'row', strength: 'Emotional awareness, creativity, faith and inner sensitivity.', weakness: 'May need conscious work on emotional expression and inner balance.' },
  { id: 'practical', name: 'Practical / Action Plane', numbers: [8, 1, 6], type: 'row', strength: 'Execution, responsibility, practical follow-through and grounded effort.', weakness: 'May need conscious work on execution, consistency and practical discipline.' },
  { id: 'thought', name: 'Thought and Family Line', numbers: [4, 3, 8], type: 'column', strength: 'Learning, family responsibility, discipline and organised growth.', weakness: 'May need support with study discipline, family patterns and long-range planning.' },
  { id: 'will', name: 'Will / Determination Arrow', numbers: [9, 5, 1], type: 'column', strength: 'Strong willpower, ambition, resilience and follow-through.', weakness: 'May need to build confidence, inner direction and steady motivation.' },
  { id: 'relationship', name: 'Relationship and Support Line', numbers: [2, 7, 6], type: 'column', strength: 'Partnership awareness, creativity, support and social sensitivity.', weakness: 'May need conscious effort in trust, partnership and asking for support.' },
  { id: 'material', name: 'Material Management Arrow', numbers: [4, 5, 6], type: 'diagonal', strength: 'Ability to manage comfort, resources, routine and practical responsibilities.', weakness: 'May need balance around money, comfort, duty and daily structure.' },
  { id: 'spiritual', name: 'Spiritual / Intuitive Arrow', numbers: [2, 5, 8], type: 'diagonal', strength: 'Intuition, patience, inner balance and spiritual maturity.', weakness: 'May need grounding in faith, patience and emotional steadiness.' },
];

const yogaDefinitions = [
  { id: 'royal-159', name: 'Determination Yoga', numbers: [1, 5, 9], effect: 'A focused will pattern that supports courage, direction and goal pursuit.' },
  { id: 'creative-357', name: 'Creative Heart Yoga', numbers: [3, 5, 7], effect: 'A creative and intuitive pattern that supports expression, faith and emotional insight.' },
  { id: 'wealth-discipline-456', name: 'Material Discipline Yoga', numbers: [4, 5, 6], effect: 'A resource-management pattern that supports routine, comfort planning and practical duty.' },
  { id: 'wisdom-438', name: 'Knowledge Discipline Yoga', numbers: [4, 3, 8], effect: 'A learning-and-discipline pattern that supports study, family responsibility and structured growth.' },
  { id: 'support-276', name: 'Relationship Support Yoga', numbers: [2, 7, 6], effect: 'A relationship pattern that supports cooperation, creative bonding and helpful networks.' },
  { id: 'spiritual-258', name: 'Intuitive Balance Yoga', numbers: [2, 5, 8], effect: 'A spiritual-balance pattern that supports patience, intuition and inner maturity.' },
];

const mysticalTriangleDefinitions = [
  { id: 'triangle-369', name: 'Mystical 3-6-9 Triangle', numbers: [3, 6, 9], effect: 'A special creative-intellectual triangle used by many numerology practitioners for expression, ideas and inspired action. In the classic Lo Shu square it is read as a triangle pattern, not a straight arrow.' },
  { id: 'triangle-147', name: 'Practical 1-4-7 Triangle', numbers: [1, 4, 7], effect: 'A practical-learning triangle that links initiative, discipline and research-oriented thinking.' },
  { id: 'triangle-258', name: 'Inner Balance 2-5-8 Triangle', numbers: [2, 5, 8], effect: 'A balance triangle that links sensitivity, center and patience; this also overlaps with the intuitive Lo Shu diagonal.' },
];

const numberRelations: Record<number, { friendly: number[]; nonFriendly: number[]; neutral: number[]; colors: string[] }> = {
  1: { friendly: [1, 3, 5], nonFriendly: [6, 8], neutral: [2, 4, 7, 9], colors: ['Gold', 'Orange', 'Bright Yellow'] },
  2: { friendly: [1, 2, 7], nonFriendly: [4, 8], neutral: [3, 5, 6, 9], colors: ['White', 'Cream', 'Soft Green'] },
  3: { friendly: [1, 3, 9], nonFriendly: [4, 8], neutral: [2, 5, 6, 7], colors: ['Yellow', 'Saffron', 'Violet'] },
  4: { friendly: [1, 5, 7], nonFriendly: [2, 9], neutral: [3, 4, 6, 8], colors: ['Electric Blue', 'Grey', 'Earth Green'] },
  5: { friendly: [1, 3, 5, 6], nonFriendly: [8], neutral: [2, 4, 7, 9], colors: ['Green', 'Turquoise', 'Light Grey'] },
  6: { friendly: [3, 5, 6, 9], nonFriendly: [1, 7], neutral: [2, 4, 8], colors: ['Pink', 'White', 'Pastel Blue'] },
  7: { friendly: [1, 2, 4, 7], nonFriendly: [6, 8], neutral: [3, 5, 9], colors: ['Sea Green', 'Silver', 'Light Yellow'] },
  8: { friendly: [3, 5, 6], nonFriendly: [1, 2, 7, 9], neutral: [4, 8], colors: ['Dark Blue', 'Black', 'Deep Purple'] },
  9: { friendly: [1, 3, 5], nonFriendly: [2, 4], neutral: [6, 7, 8, 9], colors: ['Deep Orange', 'Golden Yellow', 'Bright Red'] },
};

const nameMap: Record<string, number> = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, I: 9,
  J: 1, K: 2, L: 3, M: 4, N: 5, O: 6, P: 7, Q: 8, R: 9,
  S: 1, T: 2, U: 3, V: 4, W: 5, X: 6, Y: 7, Z: 8,
};

const chaldeanMap: Record<string, number> = {
  A: 1, I: 1, J: 1, Q: 1, Y: 1,
  B: 2, K: 2, R: 2,
  C: 3, G: 3, L: 3, S: 3,
  D: 4, M: 4, T: 4,
  E: 5, H: 5, N: 5, X: 5,
  U: 6, V: 6, W: 6,
  O: 7, Z: 7,
  F: 8, P: 8,
};

const chaldeanLettersByNumber: Record<number, string[]> = {
  1: ['A', 'I', 'J', 'Q', 'Y'],
  2: ['B', 'K', 'R'],
  3: ['C', 'G', 'L', 'S'],
  4: ['D', 'M', 'T'],
  5: ['E', 'H', 'N', 'X'],
  6: ['U', 'V', 'W'],
  7: ['O', 'Z'],
  8: ['F', 'P'],
};

const driverConductorProfiles: Record<string, Partial<LoShuResult['driverConductorAnalysis']>> = {
  '9-7': {
    title: '9 x 7: The Spiritual Fighter',
    overview: 'Mars-like courage combined with Ketu-like detachment can show a person who fights for truth, reform, spiritual clarity and protection of values.',
    strengths: 'Spiritually courageous, magnetic, research-oriented, protective and willing to challenge confusion.',
    challenges: 'Intensity can become rigid, impatient or argumentative if emotional balance is missing.',
    career: 'Can suit teaching, research, strategy, defence, spiritual education, counselling, investigation or reform-oriented work.',
    relationship: 'Needs respect for independence, honest communication and enough emotional softness to avoid sounding too sharp.',
    remedy: 'Practice disciplined action, grounding routines, humility, and service-based spiritual work.',
  },
};

const numberAnalysisProfiles: Record<number, {
  archetype: string;
  instinct: string;
  path: string;
  strength: string;
  challenge: string;
  career: string;
  relationship: string;
  practice: string;
}> = {
  1: {
    archetype: 'Independent Initiator',
    instinct: 'takes charge quickly, prefers direct answers and wants visible progress',
    path: 'builds identity through leadership, self-confidence, communication and career movement',
    strength: 'initiative, courage to begin, clarity in decision-making and the ability to open doors for others',
    challenge: 'ego clashes, impatience, loneliness or difficulty accepting help',
    career: 'leadership roles, entrepreneurship, administration, public-facing work, sales, management or independent consulting',
    relationship: 'needs respect, appreciation and room to act, while learning to listen before deciding',
    practice: 'practice humility, collaborative decision-making and steady follow-through before starting something new',
  },
  2: {
    archetype: 'Sensitive Harmoniser',
    instinct: 'responds through feeling, intuition, cooperation and the need for emotional safety',
    path: 'grows through partnership, diplomacy, patience and emotional maturity',
    strength: 'empathy, listening, bonding, negotiation and the ability to soften tense environments',
    challenge: 'over-sensitivity, dependency, mood fluctuation or avoiding direct conversations',
    career: 'counselling, client relations, hospitality, design support, HR, partnership roles or healing/spiritual services',
    relationship: 'needs reassurance, softness and consistency, while learning not to absorb every emotion',
    practice: 'build boundaries, emotional steadiness and direct but gentle communication',
  },
  3: {
    archetype: 'Expressive Teacher',
    instinct: 'processes life through learning, expression, humour, ideas and guidance',
    path: 'develops through knowledge, creativity, family responsibility and wise communication',
    strength: 'teaching, optimism, speaking, writing, mentoring and turning knowledge into useful guidance',
    challenge: 'scattered effort, over-talking, pride in opinions or unfinished learning cycles',
    career: 'education, content, training, consulting, media, writing, marketing, coaching or spiritual teaching',
    relationship: 'needs intellectual connection and honest expression, while avoiding lecturing loved ones',
    practice: 'organise ideas, complete courses/projects and use speech to uplift rather than dominate',
  },
  4: {
    archetype: 'System Builder',
    instinct: 'looks for structure, proof, order and practical control',
    path: 'learns discipline, planning, resource management and stable foundations',
    strength: 'organisation, problem-solving, persistence, process design and practical money sense',
    challenge: 'rigidity, anxiety, suspicion, shortcuts or feeling blocked by routine',
    career: 'operations, technology, analytics, finance processes, compliance, real estate, engineering or systems work',
    relationship: 'needs reliability and practical support, while learning flexibility and emotional warmth',
    practice: 'keep routines clean, avoid shortcuts, document decisions and balance control with trust',
  },
  5: {
    archetype: 'Adaptive Communicator',
    instinct: 'moves toward variety, conversation, learning, travel and quick exchange',
    path: 'matures through balance, communication, flexibility and intelligent choices',
    strength: 'networking, persuasion, adaptability, language, trade, travel and fast problem-solving',
    challenge: 'restlessness, inconsistency, over-analysis, scattered attention or impulsive choices',
    career: 'business, communication, marketing, trading, travel, product work, teaching skills or advisory roles',
    relationship: 'needs freedom and mental stimulation, while learning consistency and emotional presence',
    practice: 'use schedules, written commitments and mindful speech to stabilise fast-moving energy',
  },
  6: {
    archetype: 'Harmony Keeper',
    instinct: 'responds through care, beauty, duty, comfort and responsibility',
    path: 'develops through family harmony, refined choices, service and value-based relationships',
    strength: 'nurturing, aesthetics, responsibility, mediation, loyalty and creating comfort for others',
    challenge: 'over-giving, attachment to comfort, people-pleasing or disappointment when efforts are not returned',
    career: 'design, luxury, hospitality, counselling, education, wellness, relationship work, arts or service leadership',
    relationship: 'needs affection, loyalty and shared values, while avoiding control through care',
    practice: 'serve without over-sacrificing, simplify comforts and keep clear emotional agreements',
  },
  7: {
    archetype: 'Inner Seeker',
    instinct: 'observes deeply, questions motives and seeks hidden meaning before trusting',
    path: 'grows through research, spirituality, solitude, intuition and honest self-inquiry',
    strength: 'analysis, intuition, investigation, spiritual depth, originality and subtle perception',
    challenge: 'isolation, doubt, secrecy, overthinking or emotional distance',
    career: 'research, analytics, occult/spiritual studies, psychology, investigation, writing, technology or specialised consulting',
    relationship: 'needs trust, privacy and depth, while learning to express feelings before withdrawing',
    practice: 'balance solitude with connection, keep grounding routines and share insights simply',
  },
  8: {
    archetype: 'Karmic Organiser',
    instinct: 'measures results, responsibility, timing and long-term consequences',
    path: 'matures through patience, discipline, fairness, endurance and material responsibility',
    strength: 'hard work, management, justice, resilience, strategy and the ability to build slowly but strongly',
    challenge: 'heaviness, delay, pessimism, control issues or carrying too much responsibility alone',
    career: 'management, law, finance, governance, infrastructure, operations, manufacturing, administration or long-cycle business',
    relationship: 'needs loyalty and practical support, while learning emotional lightness and trust',
    practice: 'honour commitments, reduce resentment, plan finances and take sustainable rest',
  },
  9: {
    archetype: 'Courageous Humanitarian',
    instinct: 'acts with intensity, protection, ideals and a desire to complete what feels meaningful',
    path: 'develops through courage, service, visibility, disciplined action and wise use of power',
    strength: 'bravery, charisma, generosity, leadership in crisis, protection and high-impact action',
    challenge: 'anger, over-responsibility, dramatic reactions, impatience or fighting battles that are not theirs',
    career: 'leadership, public work, defence, coaching, social impact, sports, emergency roles, strategy or mission-led business',
    relationship: 'needs respect and honesty, while learning softness, patience and emotional repair',
    practice: 'channel intensity into service, physical discipline, forgiveness and calm speech',
  },
};

const buildDriverConductorAnalysis = (driver: number, conductor: number): LoShuResult['driverConductorAnalysis'] => {
  const profile = driverConductorProfiles[`${driver}-${conductor}`];
  if (profile?.title) return profile as LoShuResult['driverConductorAnalysis'];

  const driverProfile = numberAnalysisProfiles[driver];
  const conductorProfile = numberAnalysisProfiles[conductor];
  const harmony = driver === conductor
    ? 'Because both numbers are the same, the outer response and life direction amplify one another. This can feel powerful, but balance is important because the same strength can also become the same blind spot.'
    : `Driver ${driver} expresses as someone who ${driverProfile.instinct}, while Conductor ${conductor} asks life to mature through a path that ${conductorProfile.path}.`;

  return {
    title: `${driver} x ${conductor}: ${driverProfile.archetype} with ${conductorProfile.archetype}`,
    overview: `${harmony} The reading suggests a personality that reacts through ${driverProfile.archetype.toLowerCase()} energy and gradually learns the lessons of the ${conductorProfile.archetype.toLowerCase()}.`,
    strengths: `Natural strengths can include ${driverProfile.strength}. When the destiny number is supported, this grows into ${conductorProfile.strength}.`,
    challenges: `Watch for ${driverProfile.challenge}. The longer-term lesson may also bring ${conductorProfile.challenge}, especially during stress or major transitions.`,
    career: `Career direction is supported by ${driverProfile.career}. The conductor number adds suitability for ${conductorProfile.career}.`,
    relationship: `In relationships, the driver side ${driverProfile.relationship}. The conductor side ${conductorProfile.relationship}.`,
    remedy: `For balance: ${driverProfile.practice}. Also strengthen the conductor path: ${conductorProfile.practice}.`,
  };
};

const reduceToSingleDigit = (value: number) => {
  let sum = Math.abs(value).toString().split('').reduce((total, digit) => total + Number(digit), 0);
  while (sum > 9) sum = sum.toString().split('').reduce((total, digit) => total + Number(digit), 0);
  return sum;
};

const parseDob = (dobString: string) => {
  let year: number;
  let month: number;
  let day: number;

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dobString)) {
    [day, month, year] = dobString.split('/').map(Number);
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(dobString)) {
    [year, month, day] = dobString.split('-').map(Number);
  } else {
    throw new Error('DOB must be in DD/MM/YYYY format.');
  }

  const dob = new Date(year, month - 1, day);
  if (dob.getFullYear() !== year || dob.getMonth() !== month - 1 || dob.getDate() !== day) {
    throw new Error('DOB is not a valid calendar date.');
  }

  return { day, month, year };
};

const hasAll = (grid: Record<number, number>, numbers: number[]) => numbers.every((num) => grid[num] > 0);

const calculateNameNumber = (name?: string) => {
  const letters = String(name || '').toUpperCase().replace(/[^A-Z]/g, '').split('');
  if (!letters.length) return null;
  return reduceToSingleDigit(letters.reduce((total, letter) => total + (nameMap[letter] || 0), 0));
};

const getChaldeanNameTotal = (name: string) => (
  name.toUpperCase().replace(/[^A-Z]/g, '').split('').reduce((total, letter) => total + (chaldeanMap[letter] || 0), 0)
);

const getNaturalness = (name: string): 'Good' | 'Average' | 'Weak' => {
  const compact = name.toLowerCase().replace(/[^a-z]/g, '');
  if (/(.)\1\1/.test(compact)) return 'Weak';
  if (compact.length > 18) return 'Weak';
  if (/(.)\1/.test(compact)) return 'Average';
  return 'Good';
};

const uniqueByName = (options: LoShuNameCorrectionOption[]) => {
  const seen = new Set<string>();
  return options.filter((option) => {
    const key = option.suggestedName.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const generateLoShuNameCorrection = (
  name: string | undefined,
  missingNumbers: number[],
  driverNumber: number,
  conductorNumber: number
): LoShuNameCorrection | null => {
  const cleanName = String(name || '').trim().replace(/\s+/g, ' ');
  if (!cleanName) return null;

  const relevantMissingNumbers = missingNumbers.filter((num) => chaldeanLettersByNumber[num]?.length);
  const options: LoShuNameCorrectionOption[] = [];

  relevantMissingNumbers.forEach((missingNumber) => {
    chaldeanLettersByNumber[missingNumber].forEach((letter) => {
      const lowerLetter = letter.toLowerCase();
      const variants = [
        `${cleanName}${lowerLetter}`,
        `${cleanName}${letter}`,
      ];

      variants.forEach((suggestedName) => {
        const naturalness = getNaturalness(suggestedName);
        if (naturalness === 'Weak') return;

        const total = getChaldeanNameTotal(suggestedName);
        const nameNumber = reduceToSingleDigit(total);
        let score = naturalness === 'Good' ? 20 : 10;
        if (nameNumber === driverNumber) score += 30;
        if (nameNumber === conductorNumber) score += 30;
        if (nameNumber === missingNumber) score += 15;

        let reason = `Adds ${letter}, which carries number ${missingNumber} vibration and supports missing Lo Shu number ${missingNumber}.`;
        if (nameNumber === driverNumber) reason += ` It also matches Driver Number ${driverNumber}.`;
        if (nameNumber === conductorNumber) reason += ` It also matches Conductor Number ${conductorNumber}.`;
        if (nameNumber === missingNumber) reason += ` The resulting name number also resonates with missing number ${missingNumber}.`;

        options.push({
          suggestedName,
          addedLetter: letter,
          addedLetterNumber: missingNumber,
          total,
          nameNumber,
          supportsMissingNumber: missingNumber,
          reason,
          naturalness,
          score,
        });
      });
    });
  });

  const sortedOptions = uniqueByName(options)
    .sort((a, b) => b.score - a.score || a.suggestedName.length - b.suggestedName.length);
  const suggestions = relevantMissingNumbers.flatMap((missingNumber) => (
    sortedOptions.filter((option) => option.supportsMissingNumber === missingNumber).slice(0, 2)
  )).slice(0, 10);
  const currentTotal = getChaldeanNameTotal(cleanName);
  const currentNameNumber = currentTotal ? reduceToSingleDigit(currentTotal) : 0;
  const bestSuggestion = sortedOptions[0] || null;

  return {
    currentName: cleanName,
    currentTotal,
    currentNameNumber,
    missingNumbers: relevantMissingNumbers,
    lettersByMissingNumber: relevantMissingNumbers.reduce<Record<number, string[]>>((acc, num) => {
      acc[num] = chaldeanLettersByNumber[num];
      return acc;
    }, {}),
    suggestions,
    bestSuggestion,
    summary: bestSuggestion
      ? `${bestSuggestion.suggestedName} is the most balanced option because it supports missing number ${bestSuggestion.supportsMissingNumber}, keeps ${bestSuggestion.naturalness.toLowerCase()} spelling naturalness, and gives name number ${bestSuggestion.nameNumber}.`
      : 'No natural spelling option was generated from the current name.',
  };
};

const calculateKuaReference = (year: number) => {
  const yearRoot = reduceToSingleDigit(year);
  return reduceToSingleDigit(11 - yearRoot);
};

export const calculateLoShuNumerology = (
  dobString: string,
  options: { fullName?: string; targetDate?: string | Date } = {}
): LoShuResult => {
  const { day, month, year } = parseDob(dobString);
  const targetDate = options.targetDate ? new Date(options.targetDate) : new Date();
  const safeTarget = Number.isNaN(targetDate.getTime()) ? new Date() : targetDate;
  const rawDigits = `${String(day).padStart(2, '0')}${String(month).padStart(2, '0')}${year}`.split('').map(Number);
  const birthDigits = rawDigits.filter((digit) => digit >= 1 && digit <= 9);
  const psychicNumber = reduceToSingleDigit(day);
  const destinyNumber = reduceToSingleDigit(rawDigits.reduce((total, digit) => total + digit, 0));
  const gridDigits = [...birthDigits, psychicNumber, destinyNumber].filter((digit) => digit >= 1 && digit <= 9);
  const relation = numberRelations[psychicNumber];
  const personalYear = reduceToSingleDigit(day + month + safeTarget.getFullYear());
  const personalMonth = reduceToSingleDigit(personalYear + safeTarget.getMonth() + 1);
  const personalDay = reduceToSingleDigit(personalMonth + safeTarget.getDate());
  const luckyNumbers = Array.from(new Set([...relation.friendly, destinyNumber])).slice(0, 4);
  const grid = Array.from({ length: 9 }, (_, index) => index + 1).reduce<Record<number, number>>((acc, num) => {
    acc[num] = gridDigits.filter((digit) => digit === num).length;
    return acc;
  }, {});

  const arrows = arrowDefinitions.map((arrow) => ({
    ...arrow,
    present: hasAll(grid, arrow.numbers),
    missing: arrow.numbers.every((num) => grid[num] === 0),
    presentCount: arrow.numbers.filter((num) => grid[num] > 0).length,
  }));

  const missingNumberValues = Object.keys(grid).map(Number).filter((num) => grid[num] === 0);

  return {
    dob: dobString,
    method: 'Classic Lo Shu Grid numerology. Zeros are ignored; date digits are placed into fixed Lo Shu positions.',
    fullName: options.fullName?.trim() || undefined,
    psychicNumber,
    destinyNumber,
    kuaNumber: calculateKuaReference(year),
    personalDay,
    personalMonth,
    personalYear,
    nameNumber: calculateNameNumber(options.fullName),
    nameCorrection: generateLoShuNameCorrection(options.fullName, missingNumberValues, psychicNumber, destinyNumber),
    friendlyNumbers: relation.friendly,
    nonFriendlyNumbers: relation.nonFriendly,
    neutralNumbers: relation.neutral,
    luckyNumbers,
    luckyColors: relation.colors,
    driverConductorAnalysis: buildDriverConductorAnalysis(psychicNumber, destinyNumber),
    birthDigits,
    gridDigits,
    grid,
    layout: LO_SHU_LAYOUT,
    numbers: loShuNumberMeanings,
    presentNumbers: Object.keys(grid).map(Number).filter((num) => grid[num] > 0),
    missingNumbers: missingNumberValues.map((num) => ({ num, ...loShuNumberMeanings[num], lesson: loShuNumberMeanings[num].guidance })),
    repeatedNumbers: Object.keys(grid).map(Number).filter((num) => grid[num] > 1).map((num) => ({ num, count: grid[num], ...loShuNumberMeanings[num], interpretation: `${loShuNumberMeanings[num].title} energy is amplified ${grid[num]} times in the birth grid.` })),
    arrows,
    strengthArrows: arrows.filter((arrow) => arrow.present),
    weaknessArrows: arrows.filter((arrow) => arrow.missing),
    yogas: yogaDefinitions.filter((yoga) => hasAll(grid, yoga.numbers)),
    mysticalTriangles: mysticalTriangleDefinitions.map((triangle) => ({ ...triangle, active: hasAll(grid, triangle.numbers), presentCount: triangle.numbers.filter((num) => grid[num] > 0).length })),
    disclaimer: 'Lo Shu numerology is for spiritual guidance and self-reflection. It should not replace professional medical, legal, financial or psychological advice.',
  };
};
